/* eslint-disable import/first, stars-web/no-direct-browser-globals-access */

__webpack_public_path__ = window.__PUBLIC_PATH__ || '/';

import { shouldPolyfill as shouldPolyfillLocale } from '@formatjs/intl-locale/should-polyfill';
import { shouldPolyfill as shouldPolyfillNumberFormat } from '@formatjs/intl-numberformat/should-polyfill';
import { Cookie as CookieNames, FeatureFlag } from '@ps/web-core-enums';
import { canUseLocalStorage, integrationTesting } from '@ps/web-core-utils';
import { cashierAliases } from '@ps/web-pam-types';
import { ActiveGamesSubscription } from '@ps/web-poker-active-games';
import { RootHistoryContextProvider } from '@ps/web-shared-components';
import * as Sentry from '@sentry/react';
import { addDays } from 'date-fns';
import debug from 'debug';
import { once, random } from 'lodash';
import React, { useEffect } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import Loadable from 'react-loadable';
import ReactModal from 'react-modal';
import { BrowserRouter as Router } from 'react-router-dom';
import Cookies from 'universal-cookie';
import { v4 as uuidv4 } from 'uuid';

import { GQLClientSubscriptions } from '@swf/account';
import { WMTConfig } from '@swf/core/api/config';
import { subscribe } from '@swf/core/api/subscribe';
import AnalyticsProvider from '@swf/core/providers/AnalyticsProvider';
import AuthProvider from '@swf/core/providers/AuthProvider';
import IdpClientProvider from '@swf/core/providers/IdpClientProvider';
import SentryUserStatus from '@swf/core/providers/SentryUserStatus';
import SpadeEmbeddedAPIProvider from '@swf/core/providers/SpadeEmbeddedAPIProvider';
import store, { dispatch, getState, select } from '@swf/core/store';

import Providers from './Providers';
import Routes from './Routes';
import { getTranslationMessages } from './utils';

window.__STARS_DEBUG__ = debug;

const rootElem = document.getElementById('root') as HTMLElement;
const state = getState();
const cookies = new Cookies();
const query = select.location.getQuery(state);
const {
  sentryDsn,
  sentrySampleRate,
  sentrySampleRatePrHub,
  sentryErrorReplaySampleRate,
  sentryEnvironment
} = select.config.get(state);

const loadIntl = async () => {
  let { locale = 'en' } = window.__INITIAL_STATE__;

  if (shouldPolyfillLocale()) {
    await import('@formatjs/intl-locale/polyfill');
  }

  await import(/* webpackChunkName: "intl" */ '@formatjs/intl-numberformat/polyfill');

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (Intl.NumberFormat.polyfilled) {
    if (locale === 'zh-CN') {
      locale = 'zh-Hans';
    } else if (locale === 'zh-TW') {
      locale = 'zh-Hant';
    } else if (locale === 'pt-BR') {
      locale = 'pt';
    } else if (locale === 'no') {
      locale = 'nb';
    }

    await import(
      /* webpackInclude: /\/(es-419|zh-Hans|zh-Hant|bg|cs|da|de|el|en|es|et|fi|fr|hr|hu|it|ja|ko|lt|lv|nb|pl|pt|ro|ru|sl|sv|uk)\.js$/ */
      `@formatjs/intl-numberformat/locale-data/${locale}`
    );
  }
};

/* eslint-disable no-console */
const startup = once(async () => {
  if (!integrationTesting && sentryDsn && select.feature.check(state, FeatureFlag.EnableSentry)) {
    const isDev = sentryEnvironment === 'Development';
    Sentry.init({
      dsn: sentryDsn,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
        ...(isDev ? [Sentry.captureConsoleIntegration({ levels: ['error'] })] : [])
      ],
      environment: sentryEnvironment,
      release: window.version,
      tracesSampler: (samplingContext) => {
        if (!sentrySampleRate) {
          return 0;
        }

        /**
         * P&R Hub is preloaded on mobile applications and contributes to a large portion of the
         * spans collected, often exhausting the quota. To mitigate this, we reduce the sample rate
         */
        if (samplingContext.name.includes('prhub') && sentrySampleRatePrHub) {
          return sentrySampleRatePrHub;
        }

        return sentrySampleRate;
      },
      replaysOnErrorSampleRate: sentryErrorReplaySampleRate,
      beforeSend(event) {
        if (isDev) {
          const msg =
            event.message ||
            event.exception?.values?.[0]?.value ||
            event.exception?.values?.[0]?.type ||
            '';
          if (
            /Hydration failed|initial UI does not match|Text content does not match|error while hydrating|An error occurred during hydration/i.test(
              String(msg)
            )
          ) {
            event.tags = { ...event.tags, hydration: 'true' };
            event.fingerprint = ['react-hydration', '{{ default }}'];
          }

          const isConsole = event.logger === 'console';
          const hasException = !!event.exception?.values?.length;
          if (isConsole && !hasException) return null;
        }
        return event;
      }
    });

    if (canUseLocalStorage) {
      let sentryUserId = localStorage.getItem('sentryId');

      if (!sentryUserId) {
        sentryUserId = uuidv4();
        localStorage.setItem('sentryId', sentryUserId);
      }

      Sentry.setUser({ id: sentryUserId });
    }

    Sentry.setTag('app.brand.id', select.cms.getBrandId(state));
    Sentry.setTag('app.brand.name', select.cms.getBrandName(state));
    Sentry.setTag('app.site.id', select.cms.getSiteId(state));
    Sentry.setTag('app.site.name', select.cms.getSiteName(state));
    Sentry.setTag('app.os.id', select.device.getOS(state));
    Sentry.setTag('app.os.name', select.device.getOSName(state));
    Sentry.setTag('app.embedded', select.device.isEmbedded(state));
    Sentry.setContext('app', {
      brand: select.cms.getBrandName(state),
      site: select.cms.getSiteName(state)
    });
  }

  subscribe(WMTConfig().responseChannel).subscribe(() => undefined);
  subscribe('/user/queue/response').subscribe(() => undefined);
  const prefetch: [ReturnType<typeof getTranslationMessages>, Promise<void>] = [
    getTranslationMessages(select.locale.get(state)),
    Loadable.preloadReady()
  ];
  const domain = select.location.getCookieDomain(state);
  const embedded = select.device.isEmbedded(state);
  const { LANGUAGE, SIGNAL_ID } = CookieNames;

  cookies.set(LANGUAGE, select.locale.get(state), { domain, path: '/' });

  if (!embedded && !integrationTesting && !cookies.get(SIGNAL_ID)) {
    const signalId = random(100000000000000, 999000000000000);

    cookies.set(SIGNAL_ID, signalId, {
      domain,
      path: '/',
      expires: addDays(new Date(), 90),
      sameSite: 'none',
      secure: true
    });
  }

  if (shouldPolyfillLocale() || shouldPolyfillNumberFormat()) prefetch.push(loadIntl());
  const [messages] = await Promise.all(prefetch).catch(() => []);
  if (!select.cms.getWebsite(state)) await dispatch.cms.fetchWebsite();
  await dispatch.cms.prepare();
  dispatch.cms.preload();

  if (process.env.NODE_ENV === 'development' && !rootElem.childNodes.length) {
    await dispatch.feature.check();
    if (select.cms.isIspRouting(getState())) {
      const { sportsRoutes } = await import('@ps/web-sports-config');
      dispatch.cms.addAliases(sportsRoutes);
    }
    dispatch.cms.addAliases(cashierAliases);
  }

  const deviceTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const appTimeZone = select.timeZone.getTimeZoneName(getState());
  if (deviceTimeZone !== appTimeZone) {
    dispatch.timeZone.setTimeZone(deviceTimeZone);
  }

  ReactModal.setAppElement('#root');
  process.env.ENABLE_PROFILING && console.time && console.time('ReactRendering');

  const App: React.FC<React.PropsWithChildren<unknown>> = () => {
    useEffect(() => {
      const callback = async () => {
        process.env.ENABLE_PROFILING && console.timeEnd && console.timeEnd('ReactRendering');
        await dispatch.auth.verifySession();

        const isSports = select.cms.isSportsBrand(getState());

        if (!isSports) {
          dispatch.system.check();
        }

        dispatch.system.subscribe();

        if (process.env.NODE_ENV === 'development' && !select.cms.getPage(getState())) {
          dispatch.cms.fetchPage({ href: window.location.href });
        }
      };
      callback();
    });

    useEffect(() => {
      const callback = () => dispatch.documentVisibility.setDocumentVisibility(!document.hidden);

      document.addEventListener('visibilitychange', callback);
      return () => document.removeEventListener('visibilitychange', callback);
    }, []);

    return (
      <Providers store={store} messages={messages}>
        <HelmetProvider>
          <AnalyticsProvider>
            <GQLClientSubscriptions />
            <SentryUserStatus>
              <Router basename={select.location.getBaseURL(state)}>
                <IdpClientProvider>
                  <AuthProvider>
                    <RootHistoryContextProvider>
                      <SpadeEmbeddedAPIProvider>
                        <Routes />
                        <ActiveGamesSubscription />
                      </SpadeEmbeddedAPIProvider>
                    </RootHistoryContextProvider>
                  </AuthProvider>
                </IdpClientProvider>
              </Router>
            </SentryUserStatus>
          </AnalyticsProvider>
        </HelmetProvider>
      </Providers>
    );
  };

  rootElem.childNodes.length
    ? hydrateRoot(rootElem, <App />)
    : createRoot(rootElem).render(<App />);

  const jssStyles = document.getElementById('jss-ssr');
  if (jssStyles?.parentElement) jssStyles.parentElement.removeChild(jssStyles);
});

if (select.device.isDesktopEmbedded(state) && !query.active) {
  document.addEventListener('starsresume', startup);
} else if (select.device.isMobileEmbedded(state) && !window?.starsweb?.app.ready) {
  document.addEventListener('starsready', startup);
} else {
  startup();
}
